import React from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Stack,
    ButtonBase,
    Typography,
    useScrollTrigger,
    Drawer,
    IconButton,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    Collapse, useTheme, useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ProductsDropdownMenu from "./ProductsDropdownMenu";
import mobileDropdownOrnament from "../../../assets/icons/SM_long_flower_white.png"

/**
 * MenuBar
 *
 * Props:
 * - items: [{ id, label, to, hasDropdown?, dropdownItems? }]
 * - logoSrc: string (image path)
 * - logoAlt: string
 */
export default function MenuBar({ items = [], logoSrc, logoAlt = "Company logo" }) {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up("sm"));

    const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
    const location = useLocation();
    const navigate = useNavigate();

    const [mobileOpen, setMobileOpen] = React.useState(false);
    const openMobile = () => setMobileOpen(true);

    // Desktop dropdown (anchor + open state)
    const [dropdownAnchorEl, setDropdownAnchorEl] = React.useState(null);
    const dropdownOpen = Boolean(dropdownAnchorEl);

    // Identify the dropdown parent item (Products)
    const dropdownParent = items.find((i) => i.hasDropdown);
    const dropdownItems = dropdownParent?.dropdownItems || [];

    // Mobile expand/collapse inside drawer
    const [mobileDropdownOpen, setMobileDropdownOpen] = React.useState(false);
    const toggleMobileDropdown = () => setMobileDropdownOpen((v) => !v);

    const closeMobileAndReset = () => {
        setMobileOpen(false);
        setMobileDropdownOpen(false);
    };

    const handleLogoClick = (e) => {
        closeMobileAndReset();

        if (location.pathname === "/") {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        e.preventDefault();
        navigate("/");
        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        });
    };

    // Active highlight ONLY based on pathname (keeps your current behavior)
    const activeIndex = items.findIndex((i) => i.to === location.pathname);
    const hasActive = activeIndex !== -1;

    const closeDropdown = () => setDropdownAnchorEl(null);

    // Toggle dropdown on click (desktop only trigger)
    const toggleDropdown = (event) => {
        setDropdownAnchorEl((prev) => (prev ? null : event.currentTarget));
    };

    return (
        <Box
            sx={{
                position: "fixed",
                top: { xs: 10, sm: 16 },
                left: 0,
                right: 0,
                zIndex: (t) => t.zIndex.appBar + 10,
                pointerEvents: "none",
            }}
        >
            <Container disableGutters maxWidth={false} sx={{ pointerEvents: "none" }}>
                {/* MOBILE (xs): logo inside pill + hamburger + drawer */}
                <Box sx={{ display: { xs: "flex", sm: "none" }, pointerEvents: "none" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" sx={{ width: "100%" }}>
                        <Box
                            sx={{
                                pointerEvents: "auto",
                                mr: 1,
                                borderRadius: 999,
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                backdropFilter: "blur(5px)",
                                WebkitBackdropFilter: "blur(5px)",
                                boxShadow: scrolled
                                    ? "0 14px 40px rgba(0,0,0,0.18)"
                                    : "0 6px 18px rgba(0,0,0,0.10)",
                                transition: "box-shadow 200ms ease",
                                px: 3,
                                py: 0.75,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            {/* Logo inside the pill */}
                            <ButtonBase component={RouterLink} to="/" onClick={handleLogoClick} sx={{ borderRadius: 1 }}>
                                <Box
                                    component="img"
                                    src={logoSrc}
                                    alt={logoAlt}
                                    sx={{ height: 30, width: "auto", display: "block" }}
                                />
                            </ButtonBase>

                            {/* Hamburger */}
                            <IconButton
                                onClick={openMobile}
                                size="small"
                                sx={{
                                    color: "primary.main",
                                    backgroundColor: "rgba(255,255,255,0.35)",
                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.5)" },
                                }}
                                aria-label="Open menu"
                            >
                                <MenuRoundedIcon />
                            </IconButton>
                        </Box>
                    </Stack>
                </Box>

                {/* DESKTOP/TABLET (sm+) */}
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto 1fr",
                            alignItems: "center",
                        }}
                    >
                        {/* Left: Logo */}
                        <Box sx={{ justifySelf: "start", pointerEvents: "auto" }}>
                            <ButtonBase component={RouterLink} to="/" onClick={handleLogoClick} sx={{ borderRadius: 20, paddingLeft: 1 }}>
                                <Box
                                    component="img"
                                    src={logoSrc}
                                    alt={logoAlt}
                                    sx={{
                                        height: { xs: 28, sm: 34, md: 50, lg: 56 },
                                        width: "auto",
                                        display: "block",
                                    }}
                                />
                            </ButtonBase>
                        </Box>

                        {/* Center: Pill menu */}
                        <Box sx={{ justifySelf: "center", pointerEvents: "auto" }}>
                            <Box
                                sx={{
                                    borderRadius: 999,
                                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                                    backdropFilter: "blur(5px)",
                                    WebkitBackdropFilter: "blur(5px)",
                                    boxShadow: scrolled
                                        ? "0 14px 40px rgba(0,0,0,0.18)"
                                        : "0 6px 18px rgba(0,0,0,0.10)",
                                    transition: "box-shadow 200ms ease",
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                            >
                                <Box>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "grid",
                                            gridAutoFlow: "column",
                                            gridAutoColumns: "1fr",
                                            alignItems: "stretch",
                                        }}
                                    >
                                        {/* Sliding highlight */}
                                        {hasActive && (
                                            <Box
                                                aria-hidden
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    width: `${100 / Math.max(items.length, 1)}%`,
                                                    borderRadius: 999,
                                                    backgroundColor: "background.default",
                                                    border: "1.5px solid rgba(123, 64, 36, 0.9)",
                                                    transform: `translateX(${activeIndex * 100}%)`,
                                                    transition: "transform 260ms cubic-bezier(.2,.8,.2,1)",
                                                    zIndex: 0,
                                                }}
                                            />
                                        )}

                                        {/* Tabs */}
                                        {items.map((item) => {
                                            const isActive = item.to === location.pathname;

                                            // ✅ PRODUCTS: treat like a normal pill button on hover,
                                            // but ONLY open dropdown on click (no navigation)
                                            if (item.hasDropdown) {
                                                return (
                                                    <ButtonBase
                                                        key={item.id}
                                                        onClick={toggleDropdown}
                                                        sx={{
                                                            position: "relative",
                                                            zIndex: 1,
                                                            borderRadius: 999,
                                                            px: { xs: 1.75, sm: 2.5 },
                                                            py: { xs: 1.25, sm: 2.0 },
                                                            backgroundColor: "transparent",
                                                            border: "1.5px solid transparent",
                                                            "&:hover": {
                                                                backgroundColor: "primary.main",
                                                                "& .menu-label": { color: "#fff" },
                                                                "& .menu-arrow": { color: "#fff", opacity: 1 },
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.75,
                                                                width: "100%",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <Typography
                                                                className="menu-label"
                                                                component="span"
                                                                sx={{
                                                                    fontSize: { xs: 18, md: 20 },
                                                                    fontWeight: 900,
                                                                    letterSpacing: "0.04em",
                                                                    textTransform: "uppercase",
                                                                    color: "primary.main",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                    transition: "color 160ms ease",
                                                                }}
                                                            >
                                                                {item.label}
                                                            </Typography>

                                                            <KeyboardArrowDownRoundedIcon
                                                                className="menu-arrow"
                                                                sx={{
                                                                    fontSize: 20,
                                                                    color: "#7B4024",
                                                                    opacity: 0.9,
                                                                    transition: "color 160ms ease, opacity 160ms ease",
                                                                }}
                                                            />
                                                        </Box>
                                                    </ButtonBase>
                                                );
                                            }

                                            // ✅ Normal clickable tabs (unchanged)
                                            return (
                                                <ButtonBase
                                                    key={item.id}
                                                    component={RouterLink}
                                                    to={item.to}
                                                    sx={{
                                                        position: "relative",
                                                        zIndex: 1,
                                                        borderRadius: 999,
                                                        px: { xs: 1.75, sm: 2.5 },
                                                        py: { xs: 1.25, sm: 2.0 },
                                                        backgroundColor: "transparent",
                                                        border: "1.5px solid transparent",
                                                        ...(!isActive && {
                                                            "&:hover": {
                                                                backgroundColor: "primary.main",
                                                                "& .menu-label": {
                                                                    color: "#fff",
                                                                },
                                                            },
                                                        }),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            width: "100%",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Typography
                                                            className="menu-label"
                                                            component="span"
                                                            sx={{
                                                                fontSize: { xs: 18, md: 20 },
                                                                fontWeight: 900,
                                                                letterSpacing: "0.04em",
                                                                textTransform: "uppercase",
                                                                color: "primary.main",
                                                                lineHeight: 1,
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                    </Box>
                                                </ButtonBase>
                                            );
                                        })}
                                    </Box>

                                    {/* Desktop dropdown menu (opens ONLY on click now) */}
                                    <Menu
                                        anchorEl={dropdownAnchorEl}
                                        open={dropdownOpen}
                                        onClose={closeDropdown}
                                        slotProps={{
                                            list: {
                                                sx: { py: 0.5 },
                                            },
                                            paper: {
                                                sx: {
                                                    background: "none",
                                                    boxShadow: "none",
                                                    border: "none",
                                                    overflow: "visible",
                                                },
                                            },
                                        }}
                                    >
                                        {mdUp && (
                                            <ProductsDropdownMenu
                                                items={dropdownItems}
                                                onNavigate={closeDropdown}
                                            />
                                        )}
                                    </Menu>
                                </Box>
                            </Box>
                        </Box>

                        {/* Right: Empty column */}
                        <Box />
                    </Box>
                </Box>

                {/* MOBILE Drawer */}
                <Drawer
                    anchor="top"
                    open={mobileOpen}
                    onClose={closeMobileAndReset}
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: "rgba(255,255,255,0.9)",
                                backdropFilter: "blur(10px)",
                                WebkitBackdropFilter: "blur(10px)",
                                borderBottomLeftRadius: 24,
                                borderBottomRightRadius: 24,
                                boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                            },
                        },
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {/* Logo */}
                        <Box sx={{ pointerEvents: "auto" }}>
                            <ButtonBase component={RouterLink} to="/" onClick={handleLogoClick} sx={{ borderRadius: 5 }}>
                                <Box
                                    component="img"
                                    src={logoSrc}
                                    alt={logoAlt}
                                    sx={{ height: 60, width: "auto", display: "block" }}
                                />
                            </ButtonBase>
                        </Box>
                    </Box>

                    <Divider />

                    <List>
                        {items.map((item) => {
                            const isDropdown = Boolean(item.hasDropdown);

                            // Products dropdown on mobile (expand/collapse)
                            if (isDropdown) {
                                return (
                                    <React.Fragment key={item.id}>
                                        <ListItemButton onClick={toggleMobileDropdown} sx={{ justifyContent: "center" }}>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 900,
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.04em",
                                                            color: "primary.main",
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                }
                                            />
                                            {mobileDropdownOpen ? (
                                                <ExpandLessRoundedIcon sx={{ color: "primary.main" }} />
                                            ) : (
                                                <ExpandMoreRoundedIcon sx={{ color: "primary.main" }} />
                                            )}
                                        </ListItemButton>

                                        <Collapse in={mobileDropdownOpen} timeout="auto" unmountOnExit>
                                            <List disablePadding>
                                                {(item.dropdownItems || []).map((sub) => (
                                                    <ListItemButton
                                                        key={sub.id}
                                                        component={RouterLink}
                                                        to={sub.to}
                                                        onClick={closeMobileAndReset}
                                                        sx={{
                                                            position: "relative",          // 🔑 enables absolute children
                                                            justifyContent: "center",
                                                            py: 1.25,
                                                            backgroundColor: sub.bg,
                                                            "&:hover": { backgroundColor: sub.bg },
                                                            borderRadius: 0,
                                                            overflow: "hidden",             // 🔑 clip decorations
                                                        }}
                                                    >
                                                        {/* LEFT decorative image */}
                                                        <Box
                                                            component="img"
                                                            src={mobileDropdownOrnament}
                                                            alt=""
                                                            sx={{
                                                                position: "absolute",
                                                                left: 0,
                                                                top: "50%",
                                                                transform: "translateY(-50%) translateX(0%) rotate(90deg)",
                                                                pointerEvents: "none",
                                                                opacity: 0.7,
                                                                width: 80,
                                                            }}
                                                        />

                                                        {/* CENTER label */}
                                                        <ListItemText
                                                            sx={{ position: "relative", zIndex: 2 }}
                                                            primary={
                                                                <Typography
                                                                    align="center"
                                                                    sx={{
                                                                        fontWeight: 800,
                                                                        textTransform: "uppercase",
                                                                        letterSpacing: "0.04em",
                                                                        color: "#fff",
                                                                    }}
                                                                >
                                                                    {sub.label}
                                                                </Typography>
                                                            }
                                                        />

                                                        {/* RIGHT decorative image (mirrored) */}
                                                        <Box
                                                            component="img"
                                                            src={mobileDropdownOrnament}
                                                            alt=""
                                                            sx={{
                                                                position: "absolute",
                                                                right: 0,
                                                                top: "50%",
                                                                transform: "translateY(-50%) translateX(0%) rotate(-90deg) scaleX(-1)",
                                                                pointerEvents: "none",
                                                                opacity: 0.7,
                                                                width: 80,
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </React.Fragment>
                                );
                            }

                            // Normal items
                            const isActive = item.to === location.pathname;

                            return (
                                <ListItemButton
                                    key={item.id}
                                    selected={isActive}
                                    component={RouterLink}
                                    to={item.to}
                                    onClick={closeMobileAndReset}
                                    sx={{ justifyContent: "center" }}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography
                                                align="center"
                                                sx={{
                                                    fontWeight: 900,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    color: "primary.main",
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Drawer>
            </Container>
        </Box>
    );
}